import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider } from '@/components/react-hook-form';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  BulkImportFlowContextProvider,
  BulkImportFlowType,
} from '@/modules/import/BulkImportFlowContext';
import { EntityImportTable } from '@/modules/import/EntityImportTable';
import { EntityImportTableForm } from '@/modules/import/EntityImportTable.types';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { useIntegrationProviderName } from '../../hooks/useIntegrationProviderName';
import { EntitiesImportForm } from './hooks/useEntitiesImportForm';

export interface EntitiesImportPaneProps {
  form: EntitiesImportForm;
  onAvailabilityChange: (hasAvailableEntities: boolean) => void;
}

/**
 * "Import data" → Entities: unlinked integration items the user can bring into
 * Luminary as brand-new entities.
 *
 * `EntityImportTable` runs its own query and fills the form as it resolves, so
 * this pane only supplies the form and the chrome. The pane mounts only while
 * open, so returning to it re-runs that query — which is what drops rows that
 * have since been imported or linked.
 */
export function EntitiesImportPane({
  form,
  onAvailabilityChange,
}: EntitiesImportPaneProps) {
  const { householdId, isSaving } = useManageIntegrationDataContext();
  const { linkedHouseholdAssetProvider } = useHouseholdDetailsContext();
  const integrationProviderName = useIntegrationProviderName();

  if (!linkedHouseholdAssetProvider) {
    return null;
  }

  return (
    <BulkImportFlowContextProvider
      value={{ flowType: linkedHouseholdAssetProvider as BulkImportFlowType }}
    >
      <FormProvider<EntityImportTableForm> {...form.formMethods}>
        <FormFieldsDisabledProvider isDisabled={isSaving}>
          <EntityImportTable
            singleHouseholdId={householdId}
            emptyState={{
              heading: `All ${integrationProviderName} entities are already linked`,
              description:
                'There are no additional entities to import. To review or change an existing link, manage linked entities and linked holdings in the previous steps.',
            }}
            onAvailabilityChange={onAvailabilityChange}
            // This flow is always scoped to one household, so the table shows
            // no client grouping row for the labels to refer to.
            columnLabels={{
              unlinkedEntities: 'Unlinked entities',
              displayName: 'Entity name',
            }}
            // Matches the Liabilities and Life insurance tables this sits
            // beside, which both lead with a checkbox column.
            separateCheckboxColumn
          />
        </FormFieldsDisabledProvider>
      </FormProvider>
    </BulkImportFlowContextProvider>
  );
}
